import { useState, useEffect } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, LogOut, UserCircle, Coins, Info, MessageCircle } from 'lucide-react';
import { useAuthStore } from '@/lib/stores/authStore';
import { useSelectedCharacter } from '@/lib/stores/lobbyStore';
import { useFeedbackStore } from '@/lib/stores/feedbackStore';
import { usePmBalanceStore } from '@/lib/stores/pmBalanceStore';
import { signOut } from '@/lib/auth-service';
import { AuthDialog } from './AuthDialog';
import { useAccountModalStore } from '@/lib/stores/accountModalStore';
import { FeedbackModal } from './FeedbackModal';
import { toast } from '@/lib/toast';
import { apiClient } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_PLAYER_AVATAR } from '@/lib/media';
import { useBackendVersion } from '@/lib/version';

interface UserMenuProps {
  isCollapsed?: boolean;
}

export function UserMenu({ isCollapsed = false }: UserMenuProps) {
  const { user, isLoggedIn } = useAuthStore();
  const selectedCharacter = useSelectedCharacter();
  const playerName = user?.displayName || user?.email?.split('@')[0] || 'User';
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const openAccount = useAccountModalStore((s) => s.openModal);
  const backendVersion = useBackendVersion();
  const feedbackOpen = useFeedbackStore((s) => s.open);
  const feedbackInitialText = useFeedbackStore((s) => s.initialText);
  const openFeedback = useFeedbackStore((s) => s.openModal);
  const closeFeedback = useFeedbackStore((s) => s.closeModal);
  // Read from the shared store so this menu and the in-chat low-balance gate
  // (`ChatPanel`) agree on a single source of truth. Without this, admin
  // gift-tokens / out-of-band balance changes refresh the dropdown via the
  // REST fetch below but leave the chat gate stuck on the WS-pushed cache
  // until a hard reload.
  const pmBalance = usePmBalanceStore((s) => s.pmBalance);

  // Refetch PM balance on dropdown open and write through to the shared store.
  // `skipCache: true` busts the 1s REST cache so the menu and the gate both
  // see post-gift / post-IAP balances immediately.
  useEffect(() => {
    if (!menuOpen || !isLoggedIn) return;
    let cancelled = false;
    apiClient
      .getPmBalance({ skipCache: true })
      .then(({ pmTokenBalance }) => {
        if (!cancelled) usePmBalanceStore.getState().setPmBalance(pmTokenBalance);
      })
      .catch(() => {
        // Leave the store value as-is on failure — last WS push is still
        // better than wiping the gate to "unknown" mid-session.
      });
    return () => {
      cancelled = true;
    };
  }, [menuOpen, isLoggedIn]);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
    } catch {
      toast.error('Failed to sign out');
    }
  };

  if (!isLoggedIn) {
    return (
      <>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => setAuthOpen(true)}>
            Sign In
          </Button>
          {/* Single canonical signup page — every "Sign Up" entry point links here */}
          <Button onClick={() => navigate('/signup')}>Sign Up</Button>
        </div>
        <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      </>
    );
  }

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={`w-full h-9 justify-start ${isCollapsed ? 'justify-center px-2' : ''}`}
          >
            {/* This menu is the PLAYER's account, not their character: it opens
                account settings, billing, and sign-out. Showing the character
                name here made friend rows and DM headers (which are
                account-level, so they show player names) look inconsistent. */}
            <Avatar className={`h-5 w-5 shrink-0 ${!isCollapsed ? 'mr-3' : ''}`}>
              <AvatarImage
                src={user?.photoURL || selectedCharacter?.avatarUrl || DEFAULT_PLAYER_AVATAR}
                alt={playerName}
              />
              <AvatarFallback className="text-[10px]">
                {playerName[0]?.toUpperCase() || <User className="h-3 w-3" />}
              </AvatarFallback>
            </Avatar>
            {!isCollapsed && <span className="truncate">{playerName}</span>}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <div className="px-2 py-1.5 text-sm font-semibold truncate">{playerName}</div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/buy-tokens')}>
            <Coins className="mr-2 h-4 w-4 text-amber-500" />
            <span className="flex-1">
              {pmBalance !== null ? (
                <span className={pmBalance < 1 ? 'text-destructive' : ''}>
                  {pmBalance.toFixed(2)} PMT
                </span>
              ) : (
                <span className="text-muted-foreground">— PMT</span>
              )}
            </span>
          </DropdownMenuItem>
          <div className="flex items-center px-2 py-1.5 text-xs text-muted-foreground">
            <Info className="mr-2 h-3 w-3" />
            <span className="flex-1 font-mono">v{backendVersion}</span>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => openFeedback()}>
            <MessageCircle className="mr-2 h-4 w-4" />
            <span>Feedback</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => openAccount()}>
            <UserCircle className="mr-2 h-4 w-4" />
            <span>Account</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleSignOut}
            className="text-destructive focus:text-destructive"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Sign out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <FeedbackModal
        open={feedbackOpen}
        onOpenChange={(next) => {
          if (!next) closeFeedback();
        }}
        initialText={feedbackInitialText}
      />
    </>
  );
}
